import { getApiUrl } from "../utils/envConfig";
import {
  validateSecurityHeaders,
  getApiSecurityHeaders,
} from "../config/securityHeaders";
import { getAuthToken, refreshAuthToken } from "../utils/authUtils";
import { isUnrecoverableAuthFailure } from "../utils/authFailureCodes";
import { fetchUserData } from "./authApi";
import { extractClientId } from "../utils/extractClientId";
import axios from "axios";
import { COUNTRY_CODE_TO_NAME } from "../components/MarketOverviewPage/data/constants";
import { MODULE_SCOPES, MODULE_SCOPE_KEYS, emptyProductScopes } from "../constants/moduleScopes";
import type { components } from "./generated/schema";

// Request/response contracts sourced from the FastAPI OpenAPI schema via
// openapi-typescript (npm run gen:api-types). These types are generated
// from the backend Pydantic models, so they can't drift from what the API
// actually accepts and returns — CI fails the build if schema.ts is stale.
type UserScope = components["schemas"]["UserScope"];
type RemoveWorkspaceResponse =
  components["schemas"]["RemoveWorkspaceResponse"];
type HardDeleteUserResponse =
  components["schemas"]["HardDeleteUserResponse"];
type DeactivateUserResponse =
  components["schemas"]["DeactivateUserResponse"];
type ReactivateUserResponse =
  components["schemas"]["ReactivateUserResponse"];
type AdminResetPasswordResponse =
  components["schemas"]["AdminResetPasswordResponse"];

// Map a single module display label (one of the 5 canonical labels emitted by
// the backend) to its product_scopes key.
const MODULE_LABEL_TO_KEY = MODULE_SCOPES.reduce((acc, m) => {
  acc[m.label] = m.key;
  return acc;
}, {});

// Enhanced axios utility with token management and retry logic
export const makeAuthenticatedRequest = async (
  url,
  options: any = {},
  retries = 3
) => {
  try {
    // Always ensure we have a valid token before making the request
    const token = await getAuthToken();

    const headers = {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
      ...getApiSecurityHeaders(), // Add security headers
      ...options.headers,
    };

    const requestConfig: any = {
      url,
      headers,
      method: options.method || "GET",
      ...options,
    };

    // Handle data/body for axios
    if (options.body) {
      requestConfig.data = JSON.parse(options.body);
      delete requestConfig.body;
    }
    const response = await axios(requestConfig);

    // Validate security headers in development
    if (import.meta.env.VITE_ENVIRONMENT === "development") {
      validateSecurityHeaders(response);
    }

    return response.data;
  } catch (error) {
    // Check if this is an authentication error (401 or 403)
    const isAuthError = error.response && (error.response.status === 401 || error.response.status === 403);

    // A revoked or idle-timed-out session cannot be refreshed into a working
    // one, so sign out rather than spending the retry budget on it.
    if (
      isUnrecoverableAuthFailure(error.response?.status, error.response?.data)
    ) {
      const { handleSessionRevoked } = await import("../utils/sessionRevoked");
      await handleSessionRevoked();
      throw new Error("Session ended");
    }

    // Only retry on authentication errors
    if (isAuthError && retries > 0) {
      try {
        await refreshAuthToken();
        // Retry the request with the new token
        return await makeAuthenticatedRequest(url, options, retries - 1);
      } catch (tokenError) {
        throw new Error("Failed to refresh token: " + tokenError.message);
      }
    }

    // For non-auth errors or max retries reached, throw the original error
    if (error.response) {
      // Server responded with error. The message is left as-is, but the typed
      // `code` the API attaches to certain refusals is carried through so a
      // caller can render copy instead of a raw string. Dropping it here is
      // what forced callers to surface `error.message` verbatim.
      const thrown: any = new Error(
        error.response.data?.message || error.response.data?.error || error.message
      );
      thrown.status = error.response.status;
      thrown.code = error.response.data?.code;
      throw thrown;
    } else {
      // Network or other error
      throw error;
    }
  }
};

// Fetch users for user management page
export const fetchUsersDetailed = async (
  existingUserData = null,
  selectedWorkspace = null,
  status = "active",
  authIdClient = null
): Promise<{
  users: unknown[];
  totalCount: number;
  client: string;
  assignableRoles: string[] | null;
}> => {
  try {
    let userData, idClient;

    if (existingUserData) {
      // Use existing user data from Redux
      userData = existingUserData;
      idClient = extractClientId(userData, selectedWorkspace, authIdClient);
    } else {
      // Fallback to fetching user data
      try {
        userData = await fetchUserData(true);
        idClient = extractClientId(userData, selectedWorkspace, authIdClient);
      } catch (userDataError) {
        console.error("[fetchUsersDetailed] Error fetching user data:", userDataError);
        throw new Error("Failed to fetch user authentication data: " + userDataError.message);
      }
    }

    if (!idClient) {
      console.error("[fetchUsersDetailed] No client ID found, userData:", userData);
      throw new Error("Client ID not found in user data");
    }

    // Fetch user management data from the new endpoint. `status` selects the
    // recycle-bin view: "active" (default), "deactivated", or "all".
    const url = `${getApiUrl()}/clients/${idClient}/users/detailed?status=${encodeURIComponent(status)}`;

    try {
      const data = await makeAuthenticatedRequest(url);

      return {
        users: data.users || [],
        totalCount: data.total_count || 0,
        client: data.client || idClient,
        // The roles the CALLER may grant here, straight from the server's own
        // ladder. `null` means the answer is unknown — an absent field, or (via
        // the catch in useUserData) a failed fetch. The pickers disable
        // everything and say the permissions could not be loaded, rather than
        // guessing: a guess is how a picker comes to offer a grant the API
        // refuses, which is the defect this contract exists to remove.
        assignableRoles: Array.isArray(data.assignable_roles)
          ? data.assignable_roles
          : null,
      };
    } catch (apiError) {
      console.error("[fetchUsersDetailed] API request failed:", apiError);
      throw new Error("API request failed: " + apiError.message);
    }
  } catch (error) {
    console.error("[fetchUsersDetailed] Overall error:", error);
    throw new Error("Failed to fetch users detailed: " + error.message);
  }
};

// Fetch users grouped by market for Market View
export const fetchUsersByMarket = async (
  existingUserData = null,
  selectedWorkspace = null
): Promise<{
  marketGroups: unknown[];
  totalMarkets: number;
  totalUsers: number;
  client: string;
}> => {
  try {
    let userData, idClient;
    if (existingUserData) {
      // Use existing user data from Redux
      userData = existingUserData;
      idClient = extractClientId(userData, selectedWorkspace);
    } else {
      try {
        userData = await fetchUserData(true);
        idClient = extractClientId(userData, selectedWorkspace);
      } catch (userDataError) {
        throw new Error(
          "Failed to fetch user authentication data: " + userDataError.message
        );
      }
    }

    if (!idClient) {
      throw new Error("Client ID not found in user data");
    }

    // Fetch market-grouped users from the new endpoint
    const url = `${getApiUrl()}/clients/${idClient}/users/by-market`;

    try {
      const data = await makeAuthenticatedRequest(url);

      return {
        marketGroups: data.market_groups || [],
        totalMarkets: data.total_markets || 0,
        totalUsers: data.total_users || 0,
        client: data.client || idClient,
      };
    } catch (apiError) {
      throw new Error("API request failed: " + apiError.message);
    }
  } catch (error) {
    throw new Error("Failed to fetch users by market: " + error.message);
  }
};

// Transform API response to match table format expectations
export const transformUsersForTable = (usersApiData) => {
  const { users, totalCount, client } = usersApiData;

  // First, group users by userId to calculate proper indices for rowSpan calculation
  const usersByUserId = {};
  users.forEach((user) => {
    if (!usersByUserId[user.id_user]) {
      usersByUserId[user.id_user] = [];
    }
    usersByUserId[user.id_user].push(user);
  });

  // Calculate roleIndex and productIndex for each user's records
  const userRoleProductMap = {};
  Object.keys(usersByUserId).forEach((userId) => {
    const userRecords = usersByUserId[userId];
    userRoleProductMap[userId] = {};

    // Group by role
    const roleGroups = {};
    userRecords.forEach((record) => {
      const roleKey = record.role || 'default';
      if (!roleGroups[roleKey]) {
        roleGroups[roleKey] = [];
      }
      roleGroups[roleKey].push(record);
    });

    // Assign roleIndex and productIndex
    let roleIndex = 0;
    Object.keys(roleGroups).forEach((roleKey) => {
      const roleRecords = roleGroups[roleKey];
      userRoleProductMap[userId][roleKey] = {
        roleIndex,
        products: {},
      };

      // Group by module within this role
      let productIndex = 0;
      roleRecords.forEach((record) => {
        const productKey = record.module || 'default';
        if (!userRoleProductMap[userId][roleKey].products[productKey]) {
          userRoleProductMap[userId][roleKey].products[productKey] = productIndex++;
        }
      });

      roleIndex++;
    });
  });

  // Generate stable keys and assign proper indices
  const transformedUsers = users.map((user, index) => {
    // Create a stable key using id_user, module, and market
    const normalizeForKey = (str) => {
      if (!str) return `item-${index}`;
      return str.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
    };

    const productPart = normalizeForKey(user.module);
    const marketPart = normalizeForKey(user.market);
    const stableKey = `${user.id_user}-${productPart}-${marketPart}`;

    // Get roleIndex and productIndex from the calculated map
    const roleKey = user.role || 'default';
    const productKey = user.module || 'default';
    const roleInfo = userRoleProductMap[user.id_user]?.[roleKey];
    const roleIndex = roleInfo?.roleIndex ?? 0;
    const productIndex = roleInfo?.products[productKey] ?? 0;

    // Calculate totals for rowSpan calculation
    const totalRolesForUser = Object.keys(userRoleProductMap[user.id_user] || {}).length;
    const totalProductsForRole = Object.keys(roleInfo?.products || {}).length;

    return {
      key: stableKey,
      userId: user.id_user,
      userKey: `user-${user.id_user}`,

      // User information
      name: user.name,
      email: user.email,
      avatar: null,
      // Soft-delete state: rows from the Deactivated view carry a
      // delete_time. isDeactivated drives the "Deactivated" badge and swaps
      // the row actions (Reactivate / permanent Delete) in useUserTableColumns.
      delete_time: user.delete_time ?? null,
      isDeactivated: Boolean(user.delete_time),
      status: user.delete_time ? "Deactivated" : "Active",
      lastLogin: null,

      // Role information
      roleId: user.role?.toLowerCase().replace(/\s+/g, '-') || 'user',
      roleName: user.role || 'User',
      role: user.role || 'User',

      // Module information (displayed in Product column)
      productName: user.module || 'All modules',
      product: user.module || 'All modules',

      // Market information
      markets: user.market ? user.market.split('; ') : ['All Markets'],
      market: user.market || 'All Markets',

      // Additional metadata
      client: client,
      workspaceCount: user.workspace_count ?? 1,
      permissions: user.module ? [user.module] : ['All'],

      // Invitation lifecycle — threaded through so the Resend Invitation
      // action in useUserTableColumns.jsx can gate on it. Drop this line and
      // the field arriving from /clients/{id}/users/detailed is stripped here,
      // so the Resend button never renders.
      invitation_status: user.invitation_status ?? null,

      // Properly calculated indices for rowSpan calculation
      roleIndex,
      productIndex,
      totalRolesForUser,
      totalProductsForRole,
    };
  });

  // Sort by userId, then roleIndex, then productIndex for consistent display
  transformedUsers.sort((a, b) => {
    if (a.userId !== b.userId) {
      return a.userId.localeCompare(b.userId);
    }
    if (a.roleIndex !== b.roleIndex) {
      return a.roleIndex - b.roleIndex;
    }
    return a.productIndex - b.productIndex;
  });

  return {
    users: transformedUsers,
    totalCount,
    client,
  };
};

/**
 * Merge rows for users who have both modules into a single "All modules" row.
 * Union of markets from both rows is kept; display logic handles "All Markets".
 */
/**
 * Extract all available market codes from a regionMarketData object.
 * Markets stored as full names are converted to country codes.
 */
export const getAllMarketCodesFromRegionData = (regionMarketData: any) => {
  if (!regionMarketData || Object.keys(regionMarketData).length === 0) return [];
  return Object.values(regionMarketData).flatMap((data: any) =>
    (data.markets || []).map((m) => getCountryCode(m))
  );
};

/**
 * Merge rows for users who have BOTH modules AND ALL available markets into a
 * single "All modules" / "All Markets" row.
 * allAvailableCodes must be provided — without it no merge is performed.
 */
export const mergeAllModuleUsers = (users, allAvailableCodes = []) => {
  if (!allAvailableCodes.length) return users;

  const ALL_MODULES = MODULE_SCOPES.map((m) => m.label);
  const availableSet = new Set(allAvailableCodes);
  const result = [];
  const processed = new Set();

  const hasAllMarkets = (markets) => {
    const marketsSet = new Set(Array.isArray(markets) ? markets : []);
    return [...availableSet].every((c) => marketsSet.has(c));
  };

  users.forEach((user) => {
    if (processed.has(user.userId)) return;
    processed.add(user.userId);

    const userRows = users.filter((u) => u.userId === user.userId);
    const moduleNames = userRows.map((r) => r.productName);
    const hasAllModules = ALL_MODULES.every((m) => moduleNames.includes(m));

    if (
      hasAllModules &&
      userRows.every((r) => hasAllMarkets(r.markets))
    ) {
      const baseRow = userRows[0];
      result.push({
        ...baseRow,
        key: `${baseRow.userId}-all-modules`,
        productName: "All modules",
        product: "All modules",
        markets: [...availableSet],
        productIndex: 0,
        totalProductsForRole: 1,
        isTemporary: false,
      });
    } else {
      result.push(...userRows);
    }
  });

  return result;
};

// Build name→code map dynamically from COUNTRY_CODE_TO_NAME (single source of truth).
// Falls back to the original value so bare country codes pass through unchanged.
const COUNTRY_NAME_TO_CODE = Object.fromEntries(
  Object.entries(COUNTRY_CODE_TO_NAME).map(([code, name]) => [name, code])
);

// Convert country name to country code
const getCountryCode = (name) => COUNTRY_NAME_TO_CODE[name] || name;

// Convert market names to country codes
const convertMarketsToCountryCodes = (markets) => {
  if (!markets || !Array.isArray(markets)) return [];
  return markets.map(getCountryCode);
};

/**
 * Update user's product-based scope configuration
 * PUT /users/{id_user}/scope
 */
export const updateUserScope = async (
  idUser,
  scopeData,
  idClient = null
): Promise<UserScope> => {
  if (!idUser) {
    throw new Error("User ID is required");
  }

  let url = `${getApiUrl()}/users/${idUser}/scope`;
  if (idClient) {
    url += `?id_client=${encodeURIComponent(idClient)}`;
  }

  try {
    const data = await makeAuthenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify(scopeData),
    });

    return data;
  } catch (error) {
    // Re-throw with the typed `code` intact so the caller can map a known
    // refusal to a sentence. Wrapping in a bare Error here is what previously
    // reduced every failure to "Failed to update user scope: <raw>".
    const thrown: any = new Error(
      "Failed to update user scope: " + error.message
    );
    thrown.status = error.status;
    thrown.code = error.code;
    throw thrown;
  }
};

// Copy for a refusal the API tagged with a machine-readable code. Falls back to
// the error's own message, so an untagged failure still says something.
export const ROLE_GRANT_NOT_PERMITTED = "role_grant_not_permitted";

export const messageForScopeSaveError = (error) => {
  if (error?.code === ROLE_GRANT_NOT_PERMITTED) {
    return "Only a Super Admin of this workspace can grant that role.";
  }
  return error?.message || "Failed to save user settings";
};

/**
 * Build scope data from user editing form
 */
export const buildUserScopeData = (
  user,
  stagedChanges
) => {
  // Get the final markets (staged or original)
  const markets = stagedChanges?.markets || user.markets || [];

  // Convert market names to country codes
  const marketCodes = convertMarketsToCountryCodes(markets);

  // Get the product type
  const product = stagedChanges?.product || user.productName || "All products";

  // Role values are already backend form (super_admin / admin / normal_user).
  const backendRole = stagedChanges?.role || user.roleName || "normal_user";
  const isSuperAdmin = backendRole === "super_admin";

  // Build per-module product_scopes keyed by the 5 canonical module keys.
  // The row's `product` is now a single module label (one of the 5); map that
  // label to its key = the row's market codes, leaving the other keys empty.
  // The save-merge in the hooks unions across all of the user's rows.
  const productScopes = emptyProductScopes();

  // If super admin, product_scopes will be ignored by backend
  if (!isSuperAdmin) {
    const moduleKey = MODULE_LABEL_TO_KEY[product];
    if (moduleKey) {
      productScopes[moduleKey] = marketCodes;
    } else {
      // "All modules" / "All products" / unknown → grant every module.
      MODULE_SCOPE_KEYS.forEach((key) => {
        productScopes[key] = marketCodes;
      });
    }
  }

  return {
    is_super_admin: isSuperAdmin,
    role: backendRole,
    product_scopes: productScopes,
  };
};

/**
 * Remove a single workspace from a user without deleting the user account.
 * DELETE /users/{id_user}/workspace/{id_client}
 */
export const removeUserWorkspace = async (
  idUser,
  idClient
): Promise<RemoveWorkspaceResponse> => {
  if (!idUser || !idClient) {
    throw new Error("User ID and workspace ID are required");
  }

  try {
    const url = `${getApiUrl()}/users/${encodeURIComponent(idUser)}/workspace/${encodeURIComponent(idClient)}`;
    const data = await makeAuthenticatedRequest(url, { method: "DELETE" });
    return data;
  } catch (error) {
    console.error("[removeUserWorkspace] Error:", error);
    throw new Error("Failed to remove workspace: " + error.message);
  }
};

/**
 * Hard delete a user permanently from both Firestore and Firebase Authentication
 * DELETE /users/hard?email={email}
 *
 * This function accepts an email address and directly calls the delete endpoint
 */
export const hardDeleteUser = async (
  email
): Promise<HardDeleteUserResponse> => {
  if (!email) {
    throw new Error("User email is required");
  }

  try {
    // Call the hard delete endpoint with email as query parameter
    const url = `${getApiUrl()}/users/hard?email=${encodeURIComponent(email)}`;

    const data = await makeAuthenticatedRequest(url, {
      method: "DELETE",
    });

    return data;
  } catch (error) {
    console.error("[hardDeleteUser] Error:", error);
    throw new Error("Failed to delete user: " + error.message);
  }
};

/**
 * Soft delete (deactivate) a user — reversible.
 * DELETE /users/soft?email={email}
 *
 * Sets a delete_time flag and revokes the user's sessions server-side. The
 * account and its data are preserved; reactivateUser reverses it.
 */
export const deactivateUser = async (
  email
): Promise<DeactivateUserResponse> => {
  if (!email) {
    throw new Error("User email is required");
  }
  try {
    const url = `${getApiUrl()}/users/soft?email=${encodeURIComponent(email)}`;
    const data = await makeAuthenticatedRequest(url, { method: "DELETE" });
    return data;
  } catch (error) {
    console.error("[deactivateUser] Error:", error);
    throw new Error("Failed to deactivate user: " + error.message);
  }
};

/**
 * Reactivate a soft-deleted user — clears delete_time.
 * POST /users/reactivate?email={email}
 */
export const reactivateUser = async (
  email
): Promise<ReactivateUserResponse> => {
  if (!email) {
    throw new Error("User email is required");
  }
  try {
    const url = `${getApiUrl()}/users/reactivate?email=${encodeURIComponent(email)}`;
    const data = await makeAuthenticatedRequest(url, { method: "POST" });
    return data;
  } catch (error) {
    console.error("[reactivateUser] Error:", error);
    throw new Error("Failed to reactivate user: " + error.message);
  }
};

/**
 * Set the caller's own default workspace, the one they land in at sign-in.
 * PUT /users/me/default-workspace
 *
 * The server refuses a workspace the caller cannot open with a 422, and
 * answers 503 when it cannot read the caller's workspace list. Both reach the
 * caller as a thrown error carrying `status`.
 */
export const setOwnDefaultWorkspace = async (
  idClient: string
): Promise<{ default_id_client: string }> => {
  if (!idClient) {
    throw new Error("Workspace key is required");
  }
  const url = `${getApiUrl()}/users/me/default-workspace`;
  return makeAuthenticatedRequest(url, {
    method: "PUT",
    body: JSON.stringify({ id_client: idClient }),
  });
};

/**
 * Workspace-admin-scoped password reset for a user in the caller's workspace.
 * POST /users/workspace-reset-password
 *
 * Deliberately a direct axios call rather than makeAuthenticatedRequest:
 * (a) the FastAPI error `detail` (tenant/hierarchy 403s, Google-only 400)
 * must reach the UI verbatim, and the shared wrapper maps errors to
 * `message`/`error` fields FastAPI doesn't send; (b) the wrapper's 401/403
 * token-refresh retry loop would retry a legitimate authorization 403.
 */
export const workspaceResetPassword = async (
  email: string,
  delivery: "email" | "copy" = "email"
): Promise<AdminResetPasswordResponse> => {
  if (!email) {
    throw new Error("User email is required");
  }
  try {
    const token = await getAuthToken();
    const response = await axios({
      method: "POST",
      url: `${getApiUrl()}/users/workspace-reset-password`,
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${token}`,
        ...getApiSecurityHeaders(),
      },
      data: { email, delivery },
    });
    return response.data;
  } catch (error) {
    console.error("[workspaceResetPassword] Error:", error);
    const detail = error.response?.data?.detail;
    throw new Error(
      typeof detail === "string" ? detail : "Failed to reset the password."
    );
  }
};
